import 'react-native-get-random-values';
import '@/util/polyfillRandomUUID';
import { navigationIntegration, Sentry } from '@/util/sentry';

import { useReactQueryDevTools } from '@dev-plugins/react-query';
import { useFonts } from 'expo-font';
import '@expo/metro-runtime';
import NetInfo from '@react-native-community/netinfo';
import { onlineManager, useQuery } from '@tanstack/react-query';
import {
  SplashScreen,
  Stack,
  useGlobalSearchParams,
  useNavigationContainerRef,
  usePathname,
  useSegments,
} from 'expo-router';
import { DefaultTheme, ThemeProvider } from 'expo-router/react-navigation';
import { setOptions as setSplashScreenOptions } from 'expo-splash-screen';
import { FlagsmithProvider } from 'flagsmith/react';
import { Provider as JotaiProvider, useAtomValue } from 'jotai';
import { CircleAlert } from 'lucide-react-native';
import { cssInterop } from 'nativewind';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, LogBox, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardAwareScrollView, KeyboardProvider } from 'react-native-keyboard-controller';
import Animated from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import 'react-native-url-polyfill/auto';
import { WebView } from 'react-native-webview';

import { trackAppInitialized } from '@/analytics/app';
import { trackAppUpdatePrompted } from '@/analytics/update';
import SMSNotificationModal from '@/app/(secure)/profile/sms-notification-modal';
import { AbilityGateModal } from '@/components/AbilityGateModal';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import { MicroSurveyPopup } from '@/features/micro-survey';
import { useSurveyPrompt } from '@/features/surveys';
import { RequiredUpdateModal } from '@/components/RequiredUpdateModal';
import { ChatSessionHost } from '@/components/chat/ChatSessionHost';
import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Box } from '@/components/ui/box';
import { GluestackUIProvider } from '@/components/ui/gluestack-ui-provider';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { FONTS } from '@/constants/fonts';
import { SUPPORT_EMAIL_ADDRESS, SUPPORT_EMAIL_LINK } from '@/constants/links';
import { BASE_STACK_SCREEN_OPTIONS } from '@/constants/screenOptions';
import { THREE_SECONDS } from '@/constants/time';
import '@/global.css';
import { useInboxLiveUpdates } from '@/hooks/conversations/useInboxLiveUpdates';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import { useEmailLink } from '@/hooks/useEmailLink';
import { useShareLinkQuickActionItems } from '@/hooks/useShareLinkQuickAction';
import { useSyncSharedLink } from '@/hooks/useSyncSharedLink';
import { useUpdateManager } from '@/hooks/useUpdateManager';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import { isOnlineAtom, store, userIdAtom, authStateResolvedAtom } from '@/services/globalState';
import { initializeUserSession } from '@/services/session';
import { segmentClient } from '@/util/analytics';
import { initClarity, setClarityScreenName, setClarityUserId } from '@/util/clarity';
import { describeRouteTemplate, describeScreen } from '@/util/deepLinkAnalytics';
import { initDeepLinkTracking } from '@/util/deepLinks';
import {
  featureFlagClient,
  initializeFeatureFlagClient,
  waitForFeatureFlags,
} from '@/util/featureFlags';
import { captureFirstTouchAttribution } from '@/util/firstTouchAttribution';
import { getBuildInformation } from '@/util/getBuildInformation';
import { initOneSignal, oneSignalLogin, oneSignalLogout } from '@/util/pushNotifications';
import { queryClient, QueryClientProvider } from '@/util/queryClient';
import { prefetchStartupUser } from '@/util/startupUser';

export { ErrorBoundary } from '@/components/errorBoundary';

initializeFeatureFlagClient();

declare global {
  interface Window {
    __BUILD_INFO__?: Record<string, string | number | boolean | null | undefined>;
  }
}

if (typeof window !== 'undefined') {
  window.__BUILD_INFO__ = Object.fromEntries(
    getBuildInformation().map(({ key, value }) => [key, value])
  );
}

LogBox.ignoreLogs([
  /findDOMNode is deprecated and will be removed in the next major release/, // ignore Gluestack provider warning
]);

cssInterop(SafeAreaView, {
  className: 'style',
});

cssInterop(WebView, {
  className: 'style',
});

cssInterop(KeyboardAwareScrollView, {
  className: 'style',
  contentContainerClassName: 'contentContainerStyle',
});

cssInterop(Animated.View, {
  className: 'style',
});

onlineManager.setEventListener((setOnline) => {
  return NetInfo.addEventListener((state) => {
    const isConnected = Boolean(state.isConnected);

    store.set(isOnlineAtom, isConnected);

    setOnline(isConnected);
  });
});

initOneSignal();

initClarity();

// Capture first-touch marketing attribution from the landing URL before any
// client-side navigation can strip the query params (web-only).
captureFirstTouchAttribution();

// Report entries that came from a link we sent. Registered at module scope, beside the
// notification listener, because a link that starts a killed app arrives before any
// screen exists to hear it.
initDeepLinkTracking();

SplashScreen.preventAutoHideAsync();

/* iOS only; Android's system splash keeps its own exit. */
setSplashScreenOptions({ fade: true });

let appInitializedTracked = false;

/**
 * Router Component - App Startup & Lifecycle Orchestration
 *
 * Manages app initialization sequence: splash screen, fonts, auth check, OTA updates, and navigation.
 * Tracks APP_INITIALIZED once per session using module-level guard to prevent duplicates on remounts.
 *
 * Startup: Splash prevention → Font loading → Auth check → OTA detection → Splash hide → Navigation
 * Auth states: 'unauthenticated', 'pending_oauth', 'pending_npi_verification', 'authenticated'
 *
 * Critical behaviors: Auth completes before UI render (prevents flash); splash hides via onLayout
 * (eliminates white flash); OTA runs parallel to auth (non-blocking); screen tracking gated until ready.
 *
 * @returns Router navigation tree (null during initialization to hold splash screen)
 */
function Router() {
  const [appIsReady, setAppIsReady] = useState(false);

  const startTime = useRef(Date.now());

  const trackingRefs = useRef({
    firstScreenTracked: false,
    initializedTracked: false,
  });

  useInboxLiveUpdates();

  useSurveyPrompt();

  const { theme } = useColors();

  const isOnline = useAtomValue(isOnlineAtom);

  const { openEmailLink } = useEmailLink();

  const [fontsLoaded, fontsError] = useFonts(FONTS);

  const [fontGateExpired, setFontGateExpired] = useState(false);

  /*
   * A typeface is decoration, and decoration must never be able to withhold the app.
   *
   * Rendering below is gated on fonts, so whatever this resolves to decides whether the
   * reader sees the app at all. On web FONTS is empty and this is already satisfied on the
   * first render; on native the files ship inside the binary. The timer covers what is left:
   * expo-font batches a font map into one Promise.all whose observer takes twelve seconds to
   * reject, so waiting on `fontsError` alone would still hold a blank screen for that long,
   * and a promise that never settles would hold it forever. Text in the fallback family is a
   * far smaller loss than no text.
   */
  useEffect(() => {
    if (fontsLoaded || fontsError) {
      return;
    }

    const timer = setTimeout(() => setFontGateExpired(true), THREE_SECONDS);

    return () => clearTimeout(timer);
  }, [fontsLoaded, fontsError]);

  useEffect(() => {
    if (!fontsError) {
      return;
    }

    Sentry.captureException(fontsError, {
      tags: {
        component: 'Router',
        source: 'font-loading',
      },
      extra: {
        errorMessage: fontsError instanceof Error ? fontsError.message : String(fontsError),
      },
    });
  }, [fontsError]);

  const fontsResolved = fontsLoaded || Boolean(fontsError) || fontGateExpired;

  const { loadCurrentUser } = useAuth();

  const pathname = usePathname();

  const params = useGlobalSearchParams();

  const routeTemplate = describeRouteTemplate(useSegments());

  const {
    currentVersion,
    updateType,
    required,
    message,
    showRequiredUpdate,
    isChecking,
    isDownloading,
    isLoading: updateConfigLoading,
    appConfig,
    isUpdatePending,
  } = useUpdateManager();

  const userId = useAtomValue(userIdAtom);

  const authStateResolved = useAtomValue(authStateResolvedAtom);

  const { data: user } = useQuery(getCurrentUserScopedQueryOptions({ userId }));

  const { isPending, needsNpiVerification } = useAuthStatus();

  useSyncSharedLink();

  useShareLinkQuickActionItems();

  useEffect(() => {
    if (userId && user) {
      initializeUserSession({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        state: user.state,
        userId,
        userType: user.userType,
      });

      oneSignalLogin(userId);
    } else {
      oneSignalLogout();
    }
  }, [userId, user]);

  useEffect(() => {
    if (!appIsReady || !fontsResolved || !authStateResolved) {
      return;
    }

    /*
     * Both the screen name and the params are route data, and a route can carry a
     * credential: the magic link puts a sign-in token in the query, and the share routes
     * put a bearer token in the path. Passed through as-is, every one of those lands in
     * analytics on arrival.
     */
    const { screenName, screenParams } = describeScreen(pathname, params);

    if (!trackingRefs.current.firstScreenTracked) {
      trackingRefs.current.firstScreenTracked = true;

      segmentClient.screen(screenName, screenParams);

      return;
    }

    segmentClient.screen(screenName, screenParams);
  }, [appIsReady, fontsResolved, authStateResolved, pathname, params]);

  useEffect(() => {
    setClarityScreenName(routeTemplate);
  }, [routeTemplate]);

  useEffect(() => {
    if (!appIsReady || !authStateResolved) {
      return;
    }

    if (userId) {
      setClarityUserId(userId);
    }
  }, [appIsReady, authStateResolved, userId, pathname]);

  useEffect(() => {
    (async () => {
      const isOnOAuthCallback = pathname?.startsWith?.('/auth/callback');

      const results = await Promise.allSettled([
        waitForFeatureFlags(),
        isOnOAuthCallback ? Promise.resolve() : loadCurrentUser(),
      ]);

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const source = index === 0 ? 'feature flags' : 'current user';

          Sentry.captureException(result.reason, {
            tags: {
              component: 'Router',
              source: 'app-init',
            },
            extra: {
              initializer: source,
              errorMessage:
                result.reason instanceof Error ? result.reason.message : String(result.reason),
            },
          });
        }
      });

      await prefetchStartupUser(queryClient, store.get(userIdAtom));

      setAppIsReady(true);
    })();
  }, []);

  useEffect(() => {
    if (appInitializedTracked) {
      return;
    }

    if (!appIsReady || !authStateResolved) {
      return;
    }

    const userDataReady = !userId || isPending || user;

    if (!userDataReady) {
      return;
    }

    appInitializedTracked = true;

    trackingRefs.current.initializedTracked = true;

    const loadTimeMs = Date.now() - startTime.current;

    trackAppInitialized({
      userId,
      isPending,
      needsNpiVerification,
      user,
      hasUpdate: showRequiredUpdate,
      loadTimeMs,
    });
  }, [
    appIsReady,
    authStateResolved,
    userId,
    user,
    showRequiredUpdate,
    isPending,
    needsNpiVerification,
  ]);

  const onRootViewLayout = useCallback(async () => {
    if (!appIsReady || !fontsResolved) {
      return;
    }

    try {
      await SplashScreen.hideAsync();
    } catch {
      /* Already hidden, or there is no splash on this platform. Nothing to recover. */
    }
  }, [appIsReady, fontsResolved]);

  const { top } = useSafeAreaInsets();

  const [showUpdateActionsheet, setShowUpdateActionsheet] = useState(false);

  const [updateDismissed, setUpdateDismissed] = useState(false);

  useEffect(() => {
    if (!updateConfigLoading && showRequiredUpdate && !showUpdateActionsheet && !updateDismissed) {
      setShowUpdateActionsheet(true);

      const latestVersion = appConfig?.storeUpdate?.latestVersion;

      if (updateType) {
        trackAppUpdatePrompted({
          updateType,
          required,
          currentVersion,
          isDownloading: updateType === 'store' ? false : isDownloading,
          isUpdatePending,
          latestVersion: updateType === 'store' ? latestVersion : undefined,
        });
      }
    }
  }, [
    updateConfigLoading,
    showRequiredUpdate,
    showUpdateActionsheet,
    updateDismissed,
    updateType,
    required,
    currentVersion,
    isDownloading,
    isUpdatePending,
    appConfig,
  ]);

  const handleUpdateActionsheetClose = useCallback(() => {
    setShowUpdateActionsheet(false);

    setUpdateDismissed(true);
  }, []);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (status) => {
      if (status === 'active') {
        setUpdateDismissed(false);
      }
    });

    return () => sub.remove();
  }, []);

  const handleUpdateSuccess = useCallback(() => {
    setShowUpdateActionsheet(false);
  }, []);

  const appTheme = useMemo(() => {
    return {
      ...DefaultTheme,
      ...theme,
    };
  }, [theme]);

  if (!appIsReady || !fontsResolved) {
    /* IMPORTANT in the absence of a navigator in the RootLayout,
    sentry requires a return of null or a Slot to avoid an error */
    return null;
  }

  return (
    <Box className="flex-1" onLayout={onRootViewLayout}>
      <ThemeProvider value={appTheme}>
        <Alert
          variant="solid"
          action="error"
          className="absolute z-50 w-full flex-row items-center justify-center"
          style={{
            paddingTop: Platform.OS === 'ios' ? top : 12,
            display: isOnline ? 'none' : 'flex',
          }}>
          <AlertIcon as={CircleAlert} />

          <AlertText size="md">
            You are offline. Check your network connection, or contact{' '}
            <LinkText
              className="pl-2"
              onPress={() => {
                void openEmailLink(SUPPORT_EMAIL_LINK);
              }}>
              <Text size="sm" className="text-error-800">
                {SUPPORT_EMAIL_ADDRESS}
              </Text>
            </LinkText>
          </AlertText>
        </Alert>

        <MaintenanceBanner />

        <SMSNotificationModal />

        <MicroSurveyPopup />

        <Stack screenOptions={BASE_STACK_SCREEN_OPTIONS}>
          {/*
           * The two groups cross-fade rather than push. Every swap between them is a gate's
           * `replace`, with nothing behind it to slide back to, so a push reads as navigating
           * deeper when the reader has only changed which side of sign-in they are on. It is
           * most visible on a signed-in cold start, which opens on the landing page because
           * `/` resolves there and is then forwarded into the secure group.
           */}
          <Stack.Screen name="(open)" options={{ animation: 'fade' }} />

          <Stack.Screen name="(secure)" options={{ animation: 'fade' }} />
        </Stack>

        <AbilityGateModal />

        {showRequiredUpdate && updateType !== null ? (
          <RequiredUpdateModal
            isOpen={showUpdateActionsheet}
            updateType={updateType}
            message={message}
            isChecking={isChecking}
            isDownloading={isDownloading}
            currentVersion={currentVersion}
            isUpdatePending={isUpdatePending}
            latestVersion={appConfig?.storeUpdate?.latestVersion}
            onSuccess={handleUpdateSuccess}
          />
        ) : null}
      </ThemeProvider>
    </Box>
  );
}

function RootLayout() {
  const ref = useNavigationContainerRef();

  useReactQueryDevTools(queryClient);

  useEffect(() => {
    if (ref && navigationIntegration) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  return (
    <JotaiProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <FlagsmithProvider flagsmith={featureFlagClient}>
          <KeyboardProvider preload={false}>
            {/* Outside the Gluestack provider so its overlay hosts, where every
                sheet, modal and toast renders, sit inside the root view. */}
            <GestureHandlerRootView className="flex-1">
              <GluestackUIProvider mode="system">
                <ChatSessionHost />
                <Router />
              </GluestackUIProvider>
            </GestureHandlerRootView>
          </KeyboardProvider>
        </FlagsmithProvider>
      </QueryClientProvider>
    </JotaiProvider>
  );
}

export default Sentry.wrap(RootLayout);
